import { useCallback, useEffect, useRef, useState } from 'react'

import {
  DEFAULT_IDLE_POLICY,
  evaluateIdle,
  shouldPersistActivity,
  type IdlePolicy,
  type IdleVerdict,
} from './idle-policy'
import {
  clearLastActivity,
  readLastActivity,
  subscribeToLastActivity,
  writeLastActivity,
} from './idle-store'

export type IdleExpiry = 'idle' | 'ended'

export const IDLE_TICK_MS = 1_000

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const

const LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true }

export function markSessionActive(now: number = Date.now()): void {
  writeLastActivity(now)
}

export function endSessionActivity(): void {
  clearLastActivity()
}

export function readIdleStamp(): number | null {
  const stored = readLastActivity()
  return stored.kind === 'stamped' ? stored.at : null
}

export interface UseIdleTimeoutOptions {
  enabled: boolean
  onExpire: (expiry: IdleExpiry) => void
  policy?: IdlePolicy
}

export interface UseIdleTimeoutResult {
  readonly phase: IdleVerdict['phase']
  readonly msUntilExpiry: number
  readonly extend: () => void
}

function arrivalVerdict(enabled: boolean, policy: IdlePolicy): IdleVerdict {
  const fullWindow: IdleVerdict = { phase: 'active', msUntilExpiry: policy.timeoutMs }
  if (!enabled) return fullWindow

  const stamp = readIdleStamp()
  if (stamp === null) return fullWindow

  return evaluateIdle(stamp, Date.now(), policy)
}

export function useIdleTimeout({
  enabled,
  onExpire,
  policy = DEFAULT_IDLE_POLICY,
}: UseIdleTimeoutOptions): UseIdleTimeoutResult {
  const [verdict, setVerdict] = useState<IdleVerdict>(() => arrivalVerdict(enabled, policy))

  const [armedFor, setArmedFor] = useState(enabled)
  if (armedFor !== enabled) {
    setArmedFor(enabled)

    setVerdict(arrivalVerdict(enabled, policy))
  }

  const onExpireRef = useRef(onExpire)
  useEffect(() => {
    onExpireRef.current = onExpire
  }, [onExpire])

  const lastWriteRef = useRef<number | null>(null)
  const expiredRef = useRef(false)

  const applyVerdict = useCallback((next: IdleVerdict) => {
    setVerdict((previous) =>
      previous.phase === 'active' && next.phase === 'active' ? previous : next,
    )
  }, [])

  const recordActivity = useCallback(
    (force: boolean) => {
      if (expiredRef.current) return

      const now = Date.now()
      if (!force && !shouldPersistActivity(lastWriteRef.current, now, policy.writeThrottleMs)) {
        return
      }

      lastWriteRef.current = now
      markSessionActive(now)
      applyVerdict(evaluateIdle(now, now, policy))
    },
    [applyVerdict, policy],
  )

  const extend = useCallback(() => {
    recordActivity(true)
  }, [recordActivity])

  useEffect(() => {
    if (!enabled) {
      expiredRef.current = false
      lastWriteRef.current = null
      return
    }

    const check = () => {
      const stamp = readIdleStamp()
      const next = evaluateIdle(stamp, Date.now(), policy)
      applyVerdict(next)

      if (next.phase !== 'expired' || expiredRef.current) return

      expiredRef.current = true
      onExpireRef.current(stamp === null ? 'ended' : 'idle')
    }

    if (readIdleStamp() === null) markSessionActive()
    check()

    const timer = setInterval(check, IDLE_TICK_MS)
    const unsubscribe = subscribeToLastActivity(check)

    const onActivity = () => {
      recordActivity(false)
    }
    for (const event of ACTIVITY_EVENTS) {
      globalThis.addEventListener(event, onActivity, LISTENER_OPTIONS)
    }

    return () => {
      clearInterval(timer)
      unsubscribe()
      for (const event of ACTIVITY_EVENTS) {
        globalThis.removeEventListener(event, onActivity, LISTENER_OPTIONS)
      }
    }
  }, [enabled, policy, applyVerdict, recordActivity])

  return { phase: verdict.phase, msUntilExpiry: verdict.msUntilExpiry, extend }
}
